/**
 * Forward analytics events to native Firebase (iOS / Android WebView).
 *
 * vue-gtag's API object is Object.freeze()'d — you cannot patch api.event in place.
 * Replace Vue.$gtag / Vue.prototype.$gtag with a mutable shallow copy instead.
 *
 * Detect the native bridge (messageHandler / TeamtipAppInterface), not the UA:
 * iOS appends "TEAMTIP/…" asynchronously after load.
 *
 * Payloads must stay flat property-list types for WKWebView postMessage.
 */

function isNativeAppUa () {
  try {
    return !!(navigator && navigator.userAgent &&
      (navigator.userAgent.indexOf('TEAMTIP') > 0 || navigator.userAgent.indexOf('Teamtip') > 0))
  } catch (e) {
    return false
  }
}

/** True when the native app bridge is available (reliable on iOS + Android). */
export function hasNativeFirebaseBridge () {
  try {
    if (window.webkit && window.webkit.messageHandlers &&
        window.webkit.messageHandlers.TeamtipAppInterface) {
      return true
    }
    if (window.TeamtipAppInterface &&
        typeof window.TeamtipAppInterface.firebaseLogEvent === 'function') {
      return true
    }
  } catch (e) {
  }
  return false
}

function shouldUseNativeAnalytics () {
  return hasNativeFirebaseBridge() || isNativeAppUa()
}

function sanitizeEventName (name) {
  var eventName = String(name).replace(/[^A-Za-z0-9_]/g, '_').slice(0, 40)
  if (!/^[A-Za-z]/.test(eventName)) {
    eventName = ('E_' + eventName).slice(0, 40)
  }
  return eventName
}

function sanitizeParamValue (value) {
  if (value === null || value === undefined) {
    return null
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false'
  }
  if (typeof value === 'number') {
    if (!isFinite(value)) {
      return null
    }
    return String(value)
  }
  if (typeof value === 'string') {
    return value.slice(0, 100)
  }
  try {
    return String(value).slice(0, 100)
  } catch (e) {
    return null
  }
}

var RESERVED = { type: 1, command: 1, name: 1 }

function buildEventPayload (name, params) {
  var payload = {
    type: 'firebaseLogEvent',
    command: 'event',
    name: sanitizeEventName(name)
  }
  if (!params || typeof params !== 'object') {
    return payload
  }
  var keys = Object.keys(params)
  var count = 0
  for (var i = 0; i < keys.length && count < 25; i++) {
    var key = String(keys[i]).replace(/[^A-Za-z0-9_]/g, '_').slice(0, 40)
    if (!key || RESERVED[key]) {
      continue
    }
    var value = sanitizeParamValue(params[keys[i]])
    if (value === null) {
      continue
    }
    payload[key] = value
    count++
  }
  return payload
}

function postEventPayload (payload) {
  // Prefer JSON string on both platforms. iOS string handler parses firebaseLogEvent JSON;
  // object postMessage can drop/fail on some WebKit builds with certain param values.
  var json = null
  try {
    json = JSON.stringify(payload)
  } catch (e) {
    return
  }
  try {
    if (window.webkit && window.webkit.messageHandlers &&
        window.webkit.messageHandlers.TeamtipAppInterface) {
      window.webkit.messageHandlers.TeamtipAppInterface.postMessage(json)
    }
  } catch (err) {
  }
  try {
    if (window.TeamtipAppInterface &&
        typeof window.TeamtipAppInterface.firebaseLogEvent === 'function') {
      window.TeamtipAppInterface.firebaseLogEvent(json)
    }
  } catch (err) {
  }
}

export function logNativeFirebaseEvent (name, params) {
  try {
    if (!name || !shouldUseNativeAnalytics()) {
      return
    }
    postEventPayload(buildEventPayload(name, params))
  } catch (e) {
  }
}

/**
 * Fire an analytics event.
 * On native: always via $gtag.event / bridge (skip web CMP + gtag bootstrap).
 * On web: run webTrack() if provided (CMP + bootstrap), else gtagApi.event.
 */
export function trackAnalyticsEvent (gtagApi, name, params, webTrack) {
  try {
    if (shouldUseNativeAnalytics()) {
      if (gtagApi && typeof gtagApi.event === 'function') {
        gtagApi.event(name, params)
      } else {
        logNativeFirebaseEvent(name, params)
      }
      return
    }
  } catch (e) {
  }
  try {
    if (typeof webTrack === 'function') {
      webTrack()
      return
    }
    if (gtagApi && typeof gtagApi.event === 'function') {
      gtagApi.event(name, params)
    }
  } catch (e2) {
  }
}

/**
 * Replace frozen Vue.$gtag with a mutable copy whose event() posts to native.
 * @param {typeof Vue} VueConstructor
 */
export function wrapGtagEventForNative (VueConstructor) {
  try {
    if (!VueConstructor) {
      return
    }
    var original = VueConstructor.$gtag || VueConstructor.prototype.$gtag
    if (!original || typeof original.event !== 'function') {
      return
    }
    if (original._teamtipEventWrapped) {
      return
    }

    var originalEvent = original.event
    // Object.freeze'd api cannot be patched — copy onto a new mutable object
    var wrapped = {}
    var keys = Object.keys(original)
    for (var i = 0; i < keys.length; i++) {
      wrapped[keys[i]] = original[keys[i]]
    }

    wrapped.event = function (name, params) {
      try {
        if (shouldUseNativeAnalytics()) {
          logNativeFirebaseEvent(name, params)
          return undefined
        }
      } catch (e) {
      }
      try {
        return originalEvent.apply(original, arguments)
      } catch (e2) {
        return undefined
      }
    }
    wrapped._teamtipOriginalEvent = originalEvent
    wrapped._teamtipEventWrapped = true

    VueConstructor.$gtag = wrapped
    VueConstructor.prototype.$gtag = wrapped
  } catch (e) {
  }
}
